import * as React from "react";
import { 
    List,
    Datagrid, Filter, TopToolbar, SortButton, ExportButton,
    TextField, FunctionField,
    ReferenceInput, SelectInput,
    useRecordContext, useListSortContext
} from 'react-admin';
import { TableHead, TableRow, TableCell, useMediaQuery, makeStyles } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import get from 'lodash/get';
import Config from "../../providers/configProvider";
import LogoField from '../../components/LogoField';

const CompetitionSeasonAndTeamFilter = props => {
    const isSmall = useMediaQuery(theme => theme.breakpoints.down('sm'));
    return (
        <Filter {...props} style={{marginTop: isSmall ? '0px' : '-16px'}}>
            <ReferenceInput label="Comp Season" source="competitionSeasonId" reference="competition-season" filter={{ competitionCode: 'NRL' }} perPage={1000} alwaysOn>
                <SelectInput optionText={record => `${record.name} ${record.seasonYear}`} />
            </ReferenceInput>
            <ReferenceInput label="Team" source="teamId" reference="team" perPage={1000} alwaysOn>
                <SelectInput optionText={<LogoField type="teams" source="code" text="name" />} disabled={!props.enableTeam} />
            </ReferenceInput>
        </Filter>
    );
};

const ListActions = ({ sortFields }) => (
    <TopToolbar>
        <SortButton fields={sortFields} />
        <ExportButton />
    </TopToolbar>
);

const useStyles = makeStyles({
    cellSmall: {
        padding: '8px'
    },
    cell: {
        textAlign: 'right'
    },
    cellLeft: {
        textAlign: 'left'
    },
    cellBorder: {
        borderLeft: '1px solid #000',
        textAlign: 'right'
    },
    cellBorderLeft: {
        borderLeft: '1px solid #000',
        textAlign: 'left'
    },
    cellBorderDouble: {
        borderLeft: '3px double #000',
        textAlign: 'right'
    },
    topHeader: {
        fontWeight: 'bold',
        textAlign: 'center'
    },
    topHeaderBorder: {
        fontWeight: 'bold',
        textAlign: 'center',
        borderLeft: '1px solid #000'
    },
    topHeaderBorderDouble: {
        fontWeight: 'bold',
        textAlign: 'center',
        borderLeft: '3px double #000'
    },
    green: {
        fontWeight: 'bold',
        color: 'green'
    },
    greenSmall: {
        color: 'green'
    },
    red: {
        fontWeight: 'bold',
        color: 'red'
    },
    redSmall: {
        color: 'red'
    }
});

/*** POINTS BY QUARTER ***/
const pointsByQuarterSortFields = [
    { field: 'roundName', label: 'round' },
    { field: 'firstQuarter.for', label: '1st quarter for' }, 
    { field: 'firstQuarter.against', label: '1st quarter against' }, 
    { field: 'firstQuarter.diff', label: '1st quarter diff' }, 
    { field: 'secondQuarter.for', label: '2nd quarter for' }, 
    { field: 'secondQuarter.against', label: '2nd quarter against' }, 
    { field: 'secondQuarter.diff', label: '2nd quarter diff' }, 
    { field: 'thirdQuarter.for', label: '3rd quarter for' }, 
    { field: 'thirdQuarter.against', label: '3rd quarter against' }, 
    { field: 'thirdQuarter.diff', label: '3rd quarter diff' }, 
    { field: 'fourthQuarter.for', label: '4th quarter for' }, 
    { field: 'fourthQuarter.against', label: '4th quarter against' }, 
    { field: 'fourthQuarter.diff', label: '4th quarter diff' }, 
    { field: 'firstHalf.for', label: '1st half for' }, 
    { field: 'firstHalf.against', label: '1st half against' }, 
    { field: 'firstHalf.diff', label: '1st half diff' }, 
    { field: 'secondHalf.for', label: '2nd half for' }, 
    { field: 'secondHalf.against', label: '2nd half against' }, 
    { field: 'secondHalf.diff', label: '2nd half diff' }, 
    { field: 'extraTime.for', label: 'extra time for' }, 
    { field: 'extraTime.against', label: 'extra time against' }, 
    { field: 'extraTime.diff', label: 'extra time diff' }
];

const PointsByQuarterListRowStyle = (record, index) => ({
    backgroundColor: record.extraTime !== null ? '#f9f5de' : '#fff'
});

const PointsByQuarterHeader = ({ children }) => {
    const classes = useStyles();
    const { currentSort } = useListSortContext();
    return (
        <TableHead>
            <TableRow>
                <TableCell colSpan={2} />
                <TableCell colSpan={3} className={classes.topHeaderBorder}>1st - 20th Minute</TableCell>
                <TableCell colSpan={3} className={classes.topHeaderBorder}>21st - 40th Minute</TableCell>
                <TableCell colSpan={3} className={classes.topHeaderBorder}>41st - 60th Minute</TableCell>
                <TableCell colSpan={3} className={classes.topHeaderBorder}>61st - 80th Minute</TableCell>
                <TableCell colSpan={3} className={classes.topHeaderBorderDouble}>First Half</TableCell>
                <TableCell colSpan={3} className={classes.topHeaderBorder}>Second Half</TableCell>
                <TableCell colSpan={3} className={classes.topHeaderBorder}>Extra Time</TableCell>
            </TableRow>
            <TableRow>
                {React.Children.map(children, child => (
                    <TableCell key={child.props.source} className={child.props.source && child.props.source === 'firstHalf.for' ? classes.cellBorderDouble : (child.props.source && child.props.source.endsWith('.for') ? classes.cellBorder : (child.props.label === 'Round' || child.props.label === 'Opponent' ? classes.cellLeft : classes.cell))}>
                        {child.props.label}{pointsByQuarterSortFields.find(f => f.field === child.props.source) && currentSort.field === pointsByQuarterSortFields.find(f => f.field === child.props.source).label
                            ? (currentSort.order === 'DESC' ? <span> &darr;</span> : <span> &uarr;</span>)
                            :  null}
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
};

const PointsByQuarterForField = props => {
    const { source, threshold } = props;
    const classes = useStyles();
    const record = useRecordContext(props);
    const data = get(record, source);

    return <Typography component="span" variant="body2" className={data >= threshold ? classes.green : null}>{data}</Typography>;
}

const PointsByQuarterAgainstField = props => {
    const { source, threshold } = props;
    const classes = useStyles();
    const record = useRecordContext(props);
    const data = get(record, source);

    return <Typography component="span" variant="body2" className={data >= threshold ? classes.red : null}>{data}</Typography>;
}

export const PointsByQuarterList = props => {
    const isSmall = useMediaQuery(theme => theme.breakpoints.down('sm'));
    const classes = useStyles();
    return (
        <List {...props} 
            bulkActionButtons={false}
            filters={<CompetitionSeasonAndTeamFilter enableTeam={true} />}
            filterDefaultValues={{ competitionSeasonId: Config.CurrentCompetitionSeasonId, teamId: Config.MyTeamId }}
            actions={<ListActions sortFields={pointsByQuarterSortFields.map(f => f.label)} />}
            sort={{ field: 'round', order: 'DESC' }}
            pagination={null}
        >
            {isSmall ? (
                <Datagrid classes={{ rowCell: classes.cellSmall, headerCell: classes.cellSmall }} rowStyle={PointsByQuarterListRowStyle}>
                    <TextField source="roundNumber" label="Rd" />
                    <LogoField type="teams" source="opponentCode" text="opponentCode" label="Opp" />
                    <FunctionField render={record => (
                        <div>
                            <span className={record.firstQuarter.for >= 10 ? classes.greenSmall : null}>{record.firstQuarter.for}</span>
                            <span>-</span>
                            <span className={record.firstQuarter.against >= 10 ? classes.redSmall : null}>{record.firstQuarter.against}</span>
                        </div>
                    )} label="Q1" />
                    <FunctionField render={record => (
                        <div>
                            <span className={record.secondQuarter.for >= 10 ? classes.greenSmall : null}>{record.secondQuarter.for}</span>
                            <span>-</span>
                            <span className={record.secondQuarter.against >= 10 ? classes.redSmall : null}>{record.secondQuarter.against}</span>
                        </div>
                    )} label="Q2" />
                    <FunctionField render={record => (
                        <div>
                            <span className={record.thirdQuarter.for >= 10 ? classes.greenSmall : null}>{record.thirdQuarter.for}</span>
                            <span>-</span>
                            <span className={record.thirdQuarter.against >= 10 ? classes.redSmall : null}>{record.thirdQuarter.against}</span>
                        </div>
                    )} label="Q3" />
                    <FunctionField render={record => (
                        <div>
                            <span className={record.fourthQuarter.for >= 10 ? classes.greenSmall : null}>{record.fourthQuarter.for}</span>
                            <span>-</span>
                            <span className={record.fourthQuarter.against >= 10 ? classes.redSmall : null}>{record.fourthQuarter.against}</span>
                        </div>
                    )} label="Q4" />
                    <FunctionField render={record => (
                        <div>
                            <span className={record.firstHalf.for >= 20 ? classes.greenSmall : null}>{record.firstHalf.for}</span>
                            <span>-</span>
                            <span className={record.firstHalf.against >= 20 ? classes.redSmall : null}>{record.firstHalf.against}</span>
                        </div>
                    )} label="H1" />
                    <FunctionField render={record => (
                        <div>
                            <span className={record.secondHalf.for >= 20 ? classes.greenSmall : null}>{record.secondHalf.for}</span>
                            <span>-</span>
                            <span className={record.secondHalf.against >= 20 ? classes.redSmall : null}>{record.secondHalf.against}</span>
                            {record.extraTime === null ? null : <div className={record.extraTime.for >= 1 ? classes.greenSmall : (record.extraTime.against >= 1 ? classes.redSmall : null)}>({record.extraTime.for}-{record.extraTime.against})</div>}
                        </div>
                    )} label="H2" />
                </Datagrid>
            ) : (
                <Datagrid header={<PointsByQuarterHeader />} classes={{ rowCell: classes.cell, headerCell: classes.cell }} rowStyle={PointsByQuarterListRowStyle}>
                    <TextField source="roundName" label="Round" cellClassName={classes.cellLeft} />
                    <FunctionField render={record => record.competitionCode === 'NRL' 
                        ? <LogoField type="teams" source="opponentCode" text="opponentNickname" /> 
                        : <LogoField type="teams" source="opponentCode" text="opponentName" />
                    } label="Opponent" cellClassName={classes.cellLeft} />
                    <PointsByQuarterForField source="firstQuarter.for" threshold={10} label="F" cellClassName={classes.cellBorder} />
                    <PointsByQuarterAgainstField source="firstQuarter.against" threshold={10} label="A" />
                    <TextField source="firstQuarter.diff" label="+/-" />
                    <PointsByQuarterForField source="secondQuarter.for" threshold={10} label="F" cellClassName={classes.cellBorder} />
                    <PointsByQuarterAgainstField source="secondQuarter.against" threshold={10} label="A" />
                    <TextField source="secondQuarter.diff" label="+/-" />
                    <PointsByQuarterForField source="thirdQuarter.for" threshold={10} label="F" cellClassName={classes.cellBorder} />
                    <PointsByQuarterAgainstField source="thirdQuarter.against" threshold={10} label="A" />
                    <TextField source="thirdQuarter.diff" label="+/-" />
                    <PointsByQuarterForField source="fourthQuarter.for" threshold={10} label="F" cellClassName={classes.cellBorder} />
                    <PointsByQuarterAgainstField source="fourthQuarter.against" threshold={10} label="A" />
                    <TextField source="fourthQuarter.diff" label="+/-" />
                    <PointsByQuarterForField source="firstHalf.for" threshold={20} label="F" cellClassName={classes.cellBorderDouble} />
                    <PointsByQuarterAgainstField source="firstHalf.against" threshold={20} label="A" />
                    <TextField source="firstHalf.diff" label="+/-" />
                    <PointsByQuarterForField source="secondHalf.for" threshold={20} label="F" cellClassName={classes.cellBorder} />
                    <PointsByQuarterAgainstField source="secondHalf.against" threshold={20} label="A" />
                    <TextField source="secondHalf.diff" label="+/-" />
                    <PointsByQuarterForField source="extraTime.for" threshold={1} label="F" emptyText="-" cellClassName={classes.cellBorder} />
                    <PointsByQuarterAgainstField source="extraTime.against" threshold={1} label="A" emptyText="-" />
                    <TextField source="extraTime.diff" label="+/-" emptyText="-" />
                </Datagrid>
            )}
        </List>
    );
};
